import { from } from 'rxjs'
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators'
import { RootEpic } from '~/Store/types'
import { errorNotification } from '~/Services'
import { StaffService } from '~/Services/StaffServices'
import { OrganizationsService } from '~/Services/OrganizationService'
import { StaffActions } from './slice'

// Get staff list
const getStaffListEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(StaffActions.getStaffList.match),
    switchMap(action => {
      const { orgId, page, pageSize, filter, sort, expand } = action.payload

      // Extract filter components
      const filterString = filter?.queryString || undefined
      const roles = filter?.columnFilters?.organizationRoles || undefined
      const invitationStatus =
        filter?.columnFilters?.invitationStatus || undefined

      const filterData = {
        page,
        pageSize,
        expand,
        filter: filterString,
        orderBy: sort
          ? `${sort.field} ${sort.order === 'ascend' ? 'asc' : 'desc'}`
          : undefined,
      }

      return from(
        StaffService.Get.getOrganizationUsers(
          orgId,
          filterData,
          roles,
          invitationStatus,
        ),
      ).pipe(
        map(res =>
          StaffActions.getStaffListSuccess({
            results: (res as any).data.results,
            queryCount: (res as any).data.queryCount,
          }),
        ),
        catchError(error => {
          errorNotification(error)
          return [
            StaffActions.getStaffListFail(
              error.message || 'Failed to fetch staff list',
            ),
          ]
        }),
      )
    }),
  )

// Create staff epic
const createStaffEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(StaffActions.createStaff.match),
    switchMap(action => {
      const { orgId, data, onSuccess, onFailure } = action.payload

      return from(StaffService.Post.createNewStaff(data)).pipe(
        map(res => StaffActions.createStaffSuccess((res as any).data)),
        tap((action) => {
          if (onSuccess) onSuccess(action.payload)
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure) onFailure(error.message || 'Failed to create staff')
          return [
            StaffActions.createStaffFail(
              error.message || 'Failed to create staff',
            ),
          ]
        }),
      )
    }),
  )

// Delete staff epic
const deleteStaffEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(StaffActions.deleteStaff.match),
    switchMap(action => {
      const { orgId, userIds, onSuccess, onFailure } = action.payload

      return from(StaffService.Delete.removeUsers(orgId, userIds)).pipe(
        map(res => StaffActions.deleteStaffSuccess(userIds)),
        tap(() => {
          if (onSuccess) onSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure) onFailure(error.message || 'Failed to delete staff')
          return [
            StaffActions.deleteStaffFail(
              error.message || 'Failed to delete staff',
            ),
          ]
        }),
      )
    }),
  )

// Get roles of user epic
const getRolesEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(StaffActions.getRoles.match),
    switchMap(action => {
      const { userId, orgId } = action.payload

      return from(StaffService.Get.getRolesOfUser(userId, orgId)).pipe(
        map(res => {
          const rolesData = (res as any).data
          return StaffActions.getRolesSuccess(rolesData)
        }),
        catchError(error => {
          errorNotification(error)
          return [
            StaffActions.getRolesFail(
              error.message || 'Failed to fetch roles',
            ),
          ]
        }),
      )
    }),
  )

// Assign roles to user epic
const assignRolesEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(StaffActions.assignRoles.match),
    switchMap(action => {
      const { userId, roleData, onSuccess, onFailure } = action.payload

      return from(StaffService.Post.addRolestoUser(userId, roleData)).pipe(
        map(res => StaffActions.assignRolesSuccess()),
        tap(() => {
          if (onSuccess) onSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure) onFailure(error.message || 'Failed to assign roles')
          return [
            StaffActions.assignRolesFail(
              error.message || 'Failed to assign roles',
            ),
          ]
        }),
      )
    }),
  )

// Update check-in date
const updateCheckInDateEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(StaffActions.updateCheckInDate.match),
    switchMap(action => {
      const { userOrgId, checkInDate, onSuccess, onFailure } = action.payload

      return from(
        StaffService.Put.updateCheckInDate(userOrgId, checkInDate),
      ).pipe(
        map(res => StaffActions.updateCheckInDateSuccess()),
        tap(() => {
          if (onSuccess) onSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure) onFailure(error.message || 'Failed to assign roles')
          return [
            StaffActions.updateCheckInDateFail(
              error.message || 'Failed to assign roles',
            ),
          ]
        }),
      )
    }),
  )

// Remove roles from user epic
const removeRolesEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(StaffActions.removeRoles.match),
    switchMap(action => {
      const { userId, roleData, onSuccess, onFailure } = action.payload

      return from(
        StaffService.Post.removeRolesFromStaff(userId, roleData),
      ).pipe(
        map(res => StaffActions.removeRolesSuccess()),
        tap(() => {
          if (onSuccess) onSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure) onFailure(error.message || 'Failed to remove roles')
          return [
            StaffActions.removeRolesFail(
              error.message || 'Failed to remove roles',
            ),
          ]
        }),
      )
    }),
  )

// Export all staffs epic
const exportAllStaffsEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(StaffActions.exportAllStaffs.match),
    switchMap(action => {
      const { orgId, onSuccess, onFailure } = action.payload

      return from(
        OrganizationsService.Post.exportAllStaffsInOrganization(orgId),
      ).pipe(
        map(res => {
          if (onSuccess) onSuccess((res as any).data)
          return StaffActions.exportAllStaffsSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure)
            onFailure(error.message || 'Failed to export all staffs')
          return [
            StaffActions.exportAllStaffsFail(
              error.message || 'Failed to export all staffs',
            ),
          ]
        }),
      )
    }),
  )

// Export staffs in current page epic
const exportStaffsInCurrentPageEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(StaffActions.exportStaffsInCurrentPage.match),
    switchMap(action => {
      const {
        orgId,
        page,
        pageSize,
        filter,
        sort,
        expand,
        onSuccess,
        onFailure,
      } = action.payload
      const filterString = filter?.queryString || undefined
      const filterData = {
        page,
        pageSize,
        expand,
        filter: filterString,
        orderBy: sort
          ? `${sort.field} ${sort.order === 'ascend' ? 'asc' : 'desc'}`
          : undefined,
      }

      return from(
        OrganizationsService.Post.exportStaffsInCurrentPage(orgId, filterData),
      ).pipe(
        map(res => {
          if (onSuccess) onSuccess((res as any).data)
          return StaffActions.exportStaffsInCurrentPageSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure)
            onFailure(error.message || 'Failed to export current page staffs')
          return [
            StaffActions.exportStaffsInCurrentPageFail(
              error.message || 'Failed to export current page staffs',
            ),
          ]
        }),
      )
    }),
  )

// Export selected staffs epic
const exportSelectedStaffsEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(StaffActions.exportSelectedStaffs.match),
    switchMap(action => {
      const { orgId, userIds, onSuccess, onFailure } = action.payload

      return from(
        OrganizationsService.Post.exportSelectedStaffs(orgId, userIds),
      ).pipe(
        map(res => {
          if (onSuccess) onSuccess((res as any).data)
          return StaffActions.exportSelectedStaffsSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure)
            onFailure(error.message || 'Failed to export selected staffs')
          return [
            StaffActions.exportSelectedStaffsFail(
              error.message || 'Failed to export selected staffs',
            ),
          ]
        }),
      )
    }),
  )

// Import staffs epic
const importStaffsEpic: RootEpic = (action$, state$) =>
  action$.pipe(
    filter(StaffActions.importStaffs.match),
    switchMap(action => {
      const { orgId, file, onSuccess, onFailure } = action.payload

      return from(StaffService.Post.importStaffs(orgId, file)).pipe(
        map(res => {
          if (onSuccess) onSuccess((res as any).data)
          return StaffActions.importStaffsSuccess()
        }),
        catchError(error => {
          errorNotification(error)
          if (onFailure) onFailure(error.message || 'Failed to import staffs')
          return [
            StaffActions.importStaffsFail(
              error.message || 'Failed to import staffs',
            ),
          ]
        }),
      )
    }),
  )

export const StaffEpics = [
  getStaffListEpic,
  createStaffEpic,
  deleteStaffEpic,
  getRolesEpic,
  assignRolesEpic,
  removeRolesEpic,
  exportAllStaffsEpic,
  exportStaffsInCurrentPageEpic,
  exportSelectedStaffsEpic,
  importStaffsEpic,
  updateCheckInDateEpic,
]
